import React, { useEffect } from "react";
import {
  Avatar,
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Link,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import CallToActionIcon from "@mui/icons-material/CallToAction";
import logo from "../../assets/img/reacirc-logo.png";

const LP1 = ({ handleOpenModal }) => {
  // Track page views in Google Analytics
  useEffect(() => {
    document.title = "RealtyOS - inFlow | REACirc";

    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_title: "RealtyOS - inFlow Landing Page",
        page_location: window.location.href,
        page_path: "/",
      });
    }
  }, []);

  // Track CTA button clicks
  const handleCTAClick = () => {
    if (window.gtag) {
      window.gtag("event", "cta_click", {
        event_category: "Engagement",
        event_label: "Demo Request",
        value: 1,
      });
    }
    handleOpenModal(); // Open the contact form/modal
  };

  return (
    <Container maxWidth="lg">
      {/* Logo & Branding */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{ mt: 4 }}
      >
        <img
          src={logo}
          alt="REACirc Logo"
          style={{ width: "120px", marginBottom: "10px" }}
        />
        <Typography variant="h4" fontWeight="bold">
          RealtyOS - inFlow
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          REACirc by RealtyOS
        </Typography>
      </Box>

      {/* Hero Section */}
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h3" fontWeight="bold">
          The All-in-One Platform for Brokerage Success
        </Typography>
        <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
          Reduce admin work, boost agent collaboration, and simplify compliance—
          all in one seamless platform.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{ mt: 3 }}
          onClick={handleCTAClick}
        >
          Get a Free Personalized Demo
        </Button>
      </Box>

      {/* Pain Points & Solutions */}
      <Grid container spacing={4} sx={{ mt: 5 }}>
        {[
          {
            icon: (
              <BusinessIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
            ),
            title: "Too Many Tools?",
            description: [
              "Stop juggling multiple platforms. REACirc centralizes everything in one place:",
              "✅ Compliance tracking & document storage",
              "✅ Automated scheduling & duty management",
              "✅ Real-time agent communication & collaboration",
              "Eliminate inefficiencies and run your brokerage with ease.",
            ],
          },
          {
            icon: (
              <PeopleIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
            ),
            title: "Struggling with Agent Engagement?",
            description: [
              "Keep agents informed, connected, and engaged with integrated communication tools:",
              "✅ Trackable announcements & video updates",
              "✅ Real-time messaging & agent collaboration",
              "✅ Interactive forums to foster team culture",
            ],
          },
          {
            icon: (
              <SyncAltIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
            ),
            title: "Inefficient Operations?",
            description: [
              "Automate scheduling, compliance tracking, and admin workflows effortlessly:",
              "✅ Duty scheduling automation",
              "✅ In-house listing promotions & office tours",
              "✅ Seamless agent collaboration & compliance tracking",
              "Less manual work, smoother operations.",
            ],
          },
          {
            icon: (
              <CallToActionIcon
                fontSize="large"
                color="primary"
                sx={{ mr: 2 }}
              />
            ),
            title: "Agent Retention Challenges?",
            description: [
              "Build a brokerage culture that agents want to stay in:",
              "✅ Recognition & reward features",
              "✅ Streamlined knowledge sharing",
              "✅ Collaboration tools that foster community",
              "When agents feel supported, they stay longer.",
            ],
          },
        ].map((item, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Paper
              sx={{
                p: 3,
                m: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                height: "80%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                {item.icon}
                <Typography variant="h6" fontWeight="bold">
                  {item.title}
                </Typography>
              </Box>
              <Typography color="textSecondary" sx={{ flexGrow: 1 }}>
                {item.description.map((text, idx) => (
                  <div key={idx}>{text}</div>
                ))}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Testimonials / Social Proof */}
      <Box sx={{ textAlign: "center", py: 4, mt: 4 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
          Brokerages are Already Seeing Results with REACirc
        </Typography>

        {[
          {
            name: "Travis Carmichael",
            title:
              "President of Real Estate Operations, Royal LePage Nanaimo Realty",
            quote:
              "REACirc has been a game-changer for our brokerage. It streamlines our workflows, keeps agents engaged, and has significantly reduced administrative workload.",
            image:
              "https://dtzulyujzhqiu.cloudfront.net/royallepagenanaimorealty1942/profiles/1722450004_225197.jpg",
          },
          {
            name: "Bryan Watkins",
            title: "Royal LePage Advance Realty",
            quote:
              "Since using REACirc, our office runs much more efficiently. The automation tools save us time, and our agents feel more connected than ever.",
            image:
              "https://cdn.realtor.ca/individual/TS637399957200000000/highres/1227118.jpg",
          },
        ].map((testimonial, index) => (
          <Paper
            key={index}
            sx={{
              p: 3,
              mb: 3,
              maxWidth: "800px",
              mx: "auto",
              textAlign: "left",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Avatar
              src={testimonial.image}
              alt={testimonial.name}
              sx={{ width: 80, height: 80, mr: 3 }}
            />
            <Box>
              <Typography
                variant="body1"
                fontStyle="italic"
                color="textSecondary"
              >
                "{testimonial.quote}"
              </Typography>
              <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                - {testimonial.name}, {testimonial.title}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Final Call to Action */}
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h4" fontWeight="bold">
          Ready to Experience REACirc in Action?
        </Typography>
        <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
          Get a guided walkthrough and see how it can streamline your brokerage
          today.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{ mt: 3 }}
          onClick={handleCTAClick}
        >
          Book Your Demo Now
        </Button>
      </Box>

      {/* Footer */}
      <Box sx={{ textAlign: "center", py: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Powered By RealtyOS Inc.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          <Link href="/privacy" underline="hover" color="inherit">
            Privacy Policy
          </Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default LP1;
